import type React from 'react';
import { useId } from 'react';
import type { Novel } from '../../data/samples';
import type { NovelCoverConfig } from './NovelCard';
import { getEffectStyle, getRgba } from './cover-editor/utils/effectStyles';
import { renderFrame } from './cover-editor/utils/frames';
import { stampSvgs } from './cover-editor/utils/stamps';

interface NovelCoverProps {
  novel: Novel;
  config?: NovelCoverConfig;
  className?: string;
  isSmall?: boolean;
}

export const NovelCover: React.FC<NovelCoverProps> = ({
  novel,
  config,
  className = '',
  isSmall = false,
}) => {
  const filterIdPrefix = useId().replace(/:/g, '');

  const bgStyle: React.CSSProperties = {};
  let bgClass = `bg-gradient-to-br ${novel.coverColor}`;

  if (config) {
    if (config.bgImage) {
      bgStyle.backgroundImage = `url(${config.bgImage})`;
      bgStyle.backgroundSize = 'cover';
      bgStyle.backgroundPosition = config.bgCropPos || 'center';
      bgClass = '';
    } else if (config.bgColor) {
      const isHexOrRgb = (c: string) =>
        c.startsWith('#') || c.startsWith('rgb') || c.startsWith('hsl');
      const color1 = config.bgColor;
      const color2 = config.bgColor2;
      const color3 = config.bgColor3;

      if (isHexOrRgb(color1)) {
        bgClass = '';
        if (config.gradType === 'linear') {
          const angle = config.gradAngle ?? 135;
          bgStyle.background = color3
            ? `linear-gradient(${angle}deg, ${color1}, ${color2 || 'transparent'}, ${color3})`
            : color2
              ? `linear-gradient(${angle}deg, ${color1}, ${color2})`
              : color1;
        } else if (config.gradType === 'radial') {
          bgStyle.background = color3
            ? `radial-gradient(circle, ${color1}, ${color2 || 'transparent'}, ${color3})`
            : color2
              ? `radial-gradient(circle, ${color1}, ${color2})`
              : color1;
        } else {
          bgStyle.backgroundColor = color1;
        }
      } else {
        bgClass = `bg-gradient-to-br ${config.bgColor}`;
      }
    }
  }

  // 背景画像加工 (ブラー / 明るさ / セピア / モノクロ / 彩度 / 色相)
  const filterParts: string[] = [];
  filterParts.push(`blur(${config?.bgBlur ?? 0}px)`);
  filterParts.push(`brightness(${config?.bgBrightness ?? 100}%)`);
  if (config?.bgSepia) filterParts.push(`sepia(${config.bgSepia}%)`);
  if (config?.bgGray) filterParts.push(`grayscale(${config.bgGray}%)`);
  if (config?.bgSaturate) filterParts.push(`saturate(${config.bgSaturate}%)`);
  if (config?.bgHueRotate) filterParts.push(`hue-rotate(${config.bgHueRotate}deg)`);

  const filterBgStyle: React.CSSProperties = {
    filter: filterParts.join(' '),
  };
  if ((config?.bgBlur ?? 0) > 0) {
    filterBgStyle.transform = 'scale(1.1)'; // ブラーの余白はみ出し防止
  }

  // 複数エフェクトの構築
  const effectElements: React.ReactNode[] = [];
  if (config) {
    const list =
      config.activeEffects && config.activeEffects.length > 0
        ? config.activeEffects
        : config.effectType && config.effectType !== 'none'
          ? [
              {
                id: 'default',
                type: config.effectType,
                strength: config.effectStrength ?? 50,
                color: config.effectColor || '#ffffff',
                scale: config.effectScale || 100,
                rotate: config.effectRotate || 0,
              },
            ]
          : [];

    for (const ef of list) {
      const efScale = (ef.scale ?? 100) / 100;
      const efRotate = ef.rotate ?? 0;
      const efStyle = getEffectStyle(
        ef.type,
        ef.strength,
        ef.color,
        efScale,
        efRotate,
        config.effectOffsetX || 0,
        config.effectOffsetY || 0,
        config.effectBlendMode,
      );
      effectElements.push(
        <div
          key={ef.id || ef.type}
          style={efStyle}
          className="absolute inset-0 pointer-events-none z-5"
        />,
      );
    }
  }

  // フォントファミリー適用
  let titleFontClass = 'font-serif';
  if (config?.titleFont) {
    if (config.titleFont === 'sans') titleFontClass = 'font-sans';
    else if (config.titleFont === 'lanobe') titleFontClass = 'font-lanobe';
  }

  // タイトルサイズ適用 (isSmallの場合は強制的に小さくする)
  let titleSizeClass = isSmall ? 'text-[7px]' : 'text-xs md:text-sm';
  if (config?.titleSize && !isSmall) {
    titleSizeClass = config.titleSize;
  }

  // 色適用
  const titleStyle: React.CSSProperties = {};
  titleStyle.color = getRgba(config?.titleColor || '#ffffff', config?.titleColorOpacity ?? 100);

  // X/Yオフセット / 傾き
  const offsetX = config?.titleOffsetX ?? 0;
  const offsetY = config?.titleOffsetY ?? 0;
  const rotateDeg = config?.titleRotate ?? 0;
  // isSmallの場合、オフセットの影響を弱めるか無効にする
  titleStyle.transform = isSmall
    ? `rotate(${rotateDeg}deg)`
    : `translate(${offsetX}px, ${offsetY}px) rotate(${rotateDeg}deg)`;

  if (config?.titleLetterSpacing) {
    titleStyle.letterSpacing = `${config.titleLetterSpacing}px`;
  }

  // 縁取り (text-shadowを用いた擬似アウトライン)
  const outlineW = config?.titleOutlineWidth ?? 0;
  const outlineColor = getRgba(
    config?.titleOutlineColor || '#000000',
    config?.titleOutlineOpacity ?? 80,
  );
  let outlineStr = '';
  if (outlineW > 0) {
    outlineStr = `-${outlineW}px -${outlineW}px 0 ${outlineColor}, ${outlineW}px -${outlineW}px 0 ${outlineColor}, -${outlineW}px ${outlineW}px 0 ${outlineColor}, ${outlineW}px ${outlineW}px 0 ${outlineColor}`;
  }

  // ドロップシャドウ
  const shadowColor = getRgba(
    config?.titleShadowColor || '#000000',
    config?.titleShadowOpacity ?? 60,
  );
  const shadowBlur = config?.titleShadowBlur ?? 4;
  const shadowX = config?.titleShadowX ?? 1;
  const shadowY = config?.titleShadowY ?? 1;
  const shadowStr = `${shadowX}px ${shadowY}px ${shadowBlur}px ${shadowColor}`;

  titleStyle.textShadow = outlineStr ? `${outlineStr}, ${shadowStr}` : shadowStr;

  // 枠・下線クラス
  let titleBorderClass = '';
  if (config?.titleBorder === 'underline') {
    titleBorderClass = 'border-b pb-1';
  } else if (config?.titleBorder === 'double-underline') {
    titleBorderClass = 'border-b-4 border-double pb-1';
  } else if (config?.titleBorder === 'box') {
    titleBorderClass = 'border-2 p-1';
  }

  // タイトル背景色
  const titleBgStyle: React.CSSProperties = {};
  if (config?.titleBgType && config.titleBgType !== 'none') {
    const bgColStr = getRgba(config.titleBgColor || '#000000', config.titleBgOpacity ?? 50);

    if (config.titleBgType === 'semi-transparent' || config.titleBgType === 'solid') {
      titleBgStyle.backgroundColor = bgColStr;
      titleBgStyle.padding = '4px 8px';
      titleBgStyle.borderRadius = '4px';
    } else if (config.titleBgType === 'gradient') {
      titleBgStyle.background = `linear-gradient(transparent, ${bgColStr})`;
      titleBgStyle.padding = '12px 8px 4px 8px';
    } else if (config.titleBgType === 'fade-x') {
      titleBgStyle.background = `linear-gradient(90deg, transparent, ${bgColStr}, transparent)`;
      titleBgStyle.padding = '4px 16px';
    } else if (config.titleBgType === 'bordered') {
      titleBgStyle.backgroundColor = bgColStr;
      titleBgStyle.border = '1px solid currentColor';
      titleBgStyle.padding = '4px 8px';
    }
  }

  // タイトル位置
  let titlePosClass = 'my-auto';
  if (config?.titlePos) {
    if (config.titlePos === 'top') titlePosClass = 'mt-5 mb-auto';
    else if (config.titlePos === 'bottom') titlePosClass = 'mt-auto mb-10';
  }

  // 著者位置
  let authorPosClass = 'absolute bottom-3.5 right-4';
  if (config?.authorPos) {
    if (config.authorPos === 'top') authorPosClass = 'absolute top-3.5 right-4';
  }

  // 著者名スタイル
  const authorStyle: React.CSSProperties = {};
  authorStyle.color = getRgba(config?.authorColor || '#e2e8f0', config?.authorColorOpacity ?? 100);

  if (isSmall) {
    // 縮小版レイアウト（しおりやリスト表示用）
    return (
      <div
        style={bgStyle}
        className={`relative aspect-[3/4.2] overflow-hidden rounded-md flex flex-col justify-between p-1.5 border border-white/10 ${bgClass} ${className}`}
      >
        <div className="absolute inset-0 top-0 left-0 w-1.5 h-full bg-gradient-to-r from-black/25 via-transparent to-white/5 z-10" />

        {/* 背景画像加工レイヤー */}
        <div style={filterBgStyle} className="absolute inset-0 z-0 pointer-events-none" />

        {/* テクスチャ質感 */}
        {config?.bgTexture && config.bgTexture !== 'none' && (
          <div
            style={{
              filter: `url(#tex-${filterIdPrefix}-${config.bgTexture})`,
              opacity: (config.bgTextureOpacity ?? 40) / 100,
            }}
            className="absolute inset-0 pointer-events-none z-0"
          />
        )}

        {/* 額縁フレーム */}
        {config?.frameType &&
          renderFrame(config.frameType, config.frameColor || '#ffffff', config.frameOpacity ?? 80)}

        {/* ノイズ */}
        {config?.hasNoise && (
          <div className="absolute inset-0 opacity-15 pointer-events-none bg-[url('data:image/svg+xml;utf8,<svg viewBox=%220 0 100 100%22 xmlns=%22http://www.w3.org/2000/svg%22><filter id=%22noise%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.8%22 numOctaves=%223%22 stitchTiles=%22stitch%22/></filter><rect width=%22100%22 height=%22100%22 filter=%22url(%23noise)%22/></svg>')] bg-repeat bg-center mix-blend-overlay z-0" />
        )}

        {/* 光沢 */}
        {config?.hasGloss && (
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-transparent via-white/10 to-transparent rotate-12 scale-150 z-0" />
        )}

        {/* 高度なエフェクトレイヤー */}
        {effectElements}

        <span
          style={titleStyle}
          className={`${titleFontClass} text-[7px] md:text-[9px] leading-tight font-bold text-center block my-auto truncate drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] z-10 break-all`}
        >
          {config?.customTitle || novel.title}
        </span>
      </div>
    );
  }

  // 通常版レイアウト（本棚等のNovelCard用）
  return (
    <>
      {/* 質感テクスチャを表現するための非表示SVGフィルター定義群 */}
      {/* biome-ignore lint/a11y/noSvgWithoutTitle: filter definitions only */}
      <svg
        className="absolute w-0 h-0 opacity-0 pointer-events-none"
        style={{ visibility: 'hidden', position: 'absolute' }}
      >
        <defs>
          <filter id={`tex-${filterIdPrefix}-parchment`}>
            <feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves="3" result="noise" />
            <feDiffuseLighting in="noise" lightingColor="#f6ecdc" surfaceScale="2" result="light">
              <feDistantLight azimuth="45" elevation="60" />
            </feDiffuseLighting>
            <feBlend mode="multiply" in="SourceGraphic" in2="light" />
          </filter>
          <filter id={`tex-${filterIdPrefix}-kraft`}>
            <feTurbulence type="fractalNoise" baseFrequency="0.08" numOctaves="4" result="noise" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0.54 0 0 0 0 0.45 0 0 0 0 0.33 1 0 0 0 0"
              in="noise"
              result="coloredNoise"
            />
            <feBlend mode="multiply" in="SourceGraphic" in2="coloredNoise" />
          </filter>
          <filter id={`tex-${filterIdPrefix}-washi`}>
            <feTurbulence type="fractalNoise" baseFrequency="0.015" numOctaves="5" result="noise" />
            <feDisplacementMap
              in="SourceGraphic"
              in2="noise"
              scale="8"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
          <filter id={`tex-${filterIdPrefix}-watercolor`}>
            <feTurbulence type="fractalNoise" baseFrequency="0.03" numOctaves="2" result="noise" />
            <feDiffuseLighting in="noise" lightingColor="#ffffff" surfaceScale="3" result="light">
              <feDistantLight azimuth="120" elevation="50" />
            </feDiffuseLighting>
            <feBlend mode="multiply" in="SourceGraphic" in2="light" />
          </filter>
          <filter id={`tex-${filterIdPrefix}-canvas`}>
            <feTurbulence type="fractalNoise" baseFrequency="0.2" numOctaves="1" result="noise" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15"
              in="noise"
              result="grid"
            />
            <feBlend mode="overlay" in="SourceGraphic" in2="grid" />
          </filter>
        </defs>
      </svg>

      {/* 加工可能な背景画像/色レイヤー */}
      <div
        style={{ ...bgStyle, ...filterBgStyle }}
        className={`absolute inset-0 z-0 ${config?.isPolaroid ? 'top-2.5 left-2.5 right-2.5 bottom-12' : ''} ${bgClass}`}
      />

      {/* 質感テクスチャ */}
      {config?.bgTexture && config.bgTexture !== 'none' && (
        <div
          style={{
            filter: `url(#tex-${filterIdPrefix}-${config.bgTexture})`,
            opacity: (config.bgTextureOpacity ?? 40) / 100,
          }}
          className="absolute inset-0 pointer-events-none z-0"
        />
      )}

      {/* 額縁フレーム */}
      {config?.frameType &&
        renderFrame(config.frameType, config.frameColor || '#ffffff', config.frameOpacity ?? 80)}

      {/* デュオトーンオーバーレイ */}
      {config?.bgDuotoneEnable && (
        <div
          style={{
            background: `linear-gradient(to bottom, ${config.bgDuotoneLight || '#ffffff'}, ${config.bgDuotoneDark || '#000000'})`,
            mixBlendMode: 'color',
          }}
          className="absolute inset-0 pointer-events-none z-0"
        />
      )}

      {/* 半透明のオーバーレイカラー */}
      {config?.bgOverlayColor && (
        <div
          style={{
            backgroundColor: getRgba(config.bgOverlayColor, config.bgOverlayOpacity ?? 50),
          }}
          className="absolute inset-0 pointer-events-none z-0"
        />
      )}

      {/* ノイズテクスチャ */}
      {config?.hasNoise && (
        <div className="absolute inset-0 opacity-20 pointer-events-none bg-[url('data:image/svg+xml;utf8,<svg viewBox=%220 0 100 100%22 xmlns=%22http://www.w3.org/2000/svg%22><filter id=%22noise%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.85%22 numOctaves=%223%22 stitchTiles=%22stitch%22/></filter><rect width=%22100%22 height=%22100%22 filter=%22url(%23noise)%22/></svg>')] bg-repeat bg-center mix-blend-overlay z-0" />
      )}

      {/* 光沢グロスエフェクト */}
      {config?.hasGloss && (
        <div
          style={{ opacity: (config.glossStrength ?? 40) / 100 }}
          className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-transparent via-white/10 to-transparent rotate-12 scale-150 transform-gpu z-0"
        />
      )}

      {/* 左側タイトル帯 */}
      {config?.hasLeftTitleBar && (
        <div
          style={{
            backgroundColor: config.leftTitleBarColor || '#000000',
            opacity: (config.leftTitleBarOpacity ?? 40) / 100,
          }}
          className="absolute top-0 bottom-0 left-0 w-6 z-5 pointer-events-none"
        />
      )}

      {/* 背幅エフェクト */}
      {config?.bookSpineWidth && config.bookSpineWidth > 0 && (
        <div
          style={{
            width: `${config.bookSpineWidth}px`,
            backgroundColor: config.bookSpineColor || '#000000',
            opacity: (config.bookSpineOpacity ?? 30) / 100,
          }}
          className="absolute top-0 bottom-0 left-0 border-r border-black/20 shadow-inner z-10 pointer-events-none"
        />
      )}

      {/* 高度なエフェクトレイヤー (複数) */}
      {effectElements}

      {/* スタンプ（ステッカー）レイヤー */}
      {config?.stamps?.map((stamp) => {
        const renderSvg = stampSvgs[stamp.type];
        if (!renderSvg) return null;
        return (
          <div
            key={stamp.id}
            style={{
              left: `${stamp.x}%`,
              top: `${stamp.y}%`,
              width: '40px',
              height: '40px',
              transform: `translate(-50%, -50%) scale(${stamp.scale / 100}) rotate(${stamp.rotate}deg)`,
              opacity: stamp.opacity / 100,
              position: 'absolute',
            }}
            className="pointer-events-none z-15"
          >
            {renderSvg(stamp.color)}
          </div>
        );
      })}

      {/* フリーテキスト（追加テキスト）レイヤー */}
      {config?.customTexts?.map((txt) => {
        let fontClass = 'font-serif';
        if (txt.font === 'sans') fontClass = 'font-sans';
        else if (txt.font === 'lanobe') fontClass = 'font-lanobe';

        return (
          <div
            key={txt.id}
            style={{
              left: `${txt.x}%`,
              top: `${txt.y}%`,
              color: txt.color,
              opacity: txt.opacity / 100,
              transform: `translate(-50%, -50%) rotate(${txt.rotate}deg)`,
              fontSize: `${txt.size}px`,
              writingMode: txt.isVertical ? 'vertical-rl' : 'horizontal-tb',
              position: 'absolute',
            }}
            className={`${fontClass} font-bold leading-tight tracking-wider select-none pointer-events-none z-10 text-center drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]`}
          >
            {txt.text}
          </div>
        );
      })}

      {/* 装飾コーナー */}
      {config?.cornerStyle && config.cornerStyle !== 'none' && (
        <div
          style={{
            color: config.cornerColor || '#ffffff',
            opacity: (config.cornerOpacity ?? 70) / 100,
          }}
          className="absolute inset-0 pointer-events-none z-10 p-2"
        >
          <div className="absolute top-2 left-2 w-3 h-3 border-t border-l border-current rounded-tl-xs" />
          <div className="absolute top-2 right-2 w-3 h-3 border-t border-r border-current rounded-tr-xs" />
          <div className="absolute bottom-2 left-2 w-3 h-3 border-b border-l border-current rounded-bl-xs" />
          <div className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-current rounded-br-xs" />
        </div>
      )}

      {/* テープ装飾 */}
      {config?.tapeStyle && config.tapeStyle !== 'none' && (
        <div
          style={{
            backgroundColor: getRgba(config.tapeColor || '#ffffff', config.tapeOpacity ?? 50),
            transform: 'rotate(-4deg)',
          }}
          className={`absolute top-4 left-1/4 w-20 h-3 shadow-2xs border border-white/10 z-20 pointer-events-none opacity-80 ${
            config.tapeStyle === 'clear' ? 'backdrop-blur-xs' : ''
          }`}
        />
      )}

      {/* リボンスターバッジ */}
      {config?.badgeText && (
        <div
          style={{
            backgroundColor: getRgba(config.badgeBgColor || '#b91c1c', config.badgeBgOpacity ?? 90),
            color: config.badgeTextColor || '#ffffff',
          }}
          className={`absolute z-20 px-3 py-0.5 text-[6px] font-bold tracking-wider text-center shadow-xs ${
            config.badgePos === 'top-left'
              ? 'top-1.5 left-[-12px] -rotate-45'
              : config.badgePos === 'top-right'
                ? 'top-1.5 right-[-12px] rotate-45'
                : config.badgePos === 'bottom-left'
                  ? 'bottom-1.5 left-[-12px] rotate-45'
                  : 'bottom-1.5 right-[-12px] -rotate-45'
          }`}
        >
          {config.badgeText}
        </div>
      )}

      <div className="pointer-events-none absolute top-0 left-0 h-full w-2.5 bg-gradient-to-r from-black/25 via-transparent to-white/5 z-10" />

      {/* 著者名 */}
      <div
        style={authorStyle}
        className={`${authorPosClass} rounded-sm bg-black/25 px-2 py-0.5 pr-8 font-serif text-[9px] font-medium tracking-widest backdrop-blur-xs z-10`}
      >
        {novel.author}
      </div>

      {/* キャッチコピー */}
      {config?.copywritingText && (
        <div
          style={{
            color: getRgba(config.copywritingColor || '#ffffff', config.copywritingOpacity ?? 70),
          }}
          className={`absolute left-2 right-2 text-center tracking-widest font-serif ${
            config.copywritingSize || 'text-[7px]'
          } z-10 leading-normal ${
            config.copywritingPos === 'top'
              ? 'top-8'
              : config.copywritingPos === 'bottom'
                ? 'bottom-16'
                : 'top-1/2 -translate-y-12'
          }`}
        >
          {config.copywritingText}
        </div>
      )}

      {/* タイトル & サブタイトル */}
      <div className={`px-1 text-center z-10 ${titlePosClass}`}>
        <div style={titleStyle} className="inline-block transition-transform duration-100">
          <div style={titleBgStyle}>
            <div
              style={{ writingMode: config?.isVerticalTitle ? 'vertical-rl' : 'horizontal-tb' }}
              className={`${titleFontClass} ${titleSizeClass} ${titleBorderClass} font-bold leading-relaxed tracking-widest`}
            >
              {config?.titleRuby ? (
                <ruby>
                  {config.customTitle || novel.title}
                  <rt className="text-[7px] font-normal tracking-normal pb-0.5">
                    {config.titleRuby}
                  </rt>
                </ruby>
              ) : (
                config?.customTitle || novel.title
              )}
            </div>
            {config?.subtitleText && (
              <div
                style={{
                  color: getRgba(config.subtitleColor || '#ffffff', config.subtitleOpacity ?? 80),
                }}
                className={`block mt-1 font-serif tracking-wider ${config.subtitleSize || 'text-[8px]'} leading-tight`}
              >
                {config.subtitleText}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 帯 (Obi) */}
      {config?.obiText && (
        <div
          style={{
            backgroundColor: getRgba(config.obiBg || '#b91c1c', config.obiBgOpacity ?? 100),
            color: getRgba(config.obiColor || '#ffffff', config.obiColorOpacity ?? 100),
          }}
          className="absolute bottom-16 left-0 right-0 py-1.5 px-2 text-[9px] font-bold text-center tracking-wider shadow-sm z-10 font-serif leading-tight overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {config.obiText}
        </div>
      )}
    </>
  );
};
